/**
 * Digest Preferences Component
 *
 * Allows coaches to configure simplified digest notification preferences.
 * Implements the "one toggle, one time" model where digests are sent daily
 * at 10:00 AM in the coach's timezone.
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  getDigestPreferences,
  updateDigestPreferences,
  DigestPreferences as DigestPreferencesType,
} from '../../services/digestPreferences';
import { sendTestEmail } from '../../services/emailPreferences';

interface DigestPreferencesProps {
  className?: string;
}

const DigestPreferences: React.FC<DigestPreferencesProps> = ({ className = '' }) => {
  const { isDark } = useTheme();
  const [preferences, setPreferences] = useState<DigestPreferencesType>({
    digest_enabled: false,
    digest_timezone: 'America/New_York',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTimezoneSelector, setShowTimezoneSelector] = useState(false);

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  // Auto-detect timezone on first load
  useEffect(() => {
    // Only run once after preferences are loaded
    if (!loading && preferences.digest_enabled !== undefined) {
      // Always try to detect browser timezone on first load
      const detectedTimezone = detectUserTimezone();
      if (detectedTimezone && detectedTimezone !== preferences.digest_timezone) {
        setPreferences(prev => ({ ...prev, digest_timezone: detectedTimezone }));
      }
    }
  }, [loading]); // Only run once when loading completes

  /**
   * Detect user's timezone from browser and map to valid pytz timezone.
   * Falls back to 'America/New_York' (EST) if detection fails.
   * 
   * @returns Valid pytz timezone string
   */
  const detectUserTimezone = (): string => {
    try {
      // Get timezone from browser
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Map of common browser timezones to pytz equivalents
      const timezoneMap: Record<string, string> = {
        // US timezones
        'America/New_York': 'America/New_York',
        'America/Chicago': 'America/Chicago',
        'America/Denver': 'America/Denver',
        'America/Phoenix': 'America/Phoenix',
        'America/Los_Angeles': 'America/Los_Angeles',
        'America/Anchorage': 'America/Anchorage',
        'Pacific/Honolulu': 'Pacific/Honolulu',
        // Additional common timezones
        'US/Eastern': 'America/New_York',
        'US/Central': 'America/Chicago',
        'US/Mountain': 'America/Denver',
        'US/Pacific': 'America/Los_Angeles',
        'US/Alaska': 'America/Anchorage',
        'US/Hawaii': 'Pacific/Honolulu',
      };

      // Check if we have a direct mapping
      if (timezoneMap[browserTimezone]) {
        return timezoneMap[browserTimezone];
      }

      // Check if browser timezone is in our valid list
      const validTimezones = timezones.map(tz => tz.value);
      if (validTimezones.includes(browserTimezone)) {
        return browserTimezone;
      }

      // Fallback to EST
      return 'America/New_York';
    } catch (error) {
      console.warn('Failed to detect timezone, using EST as default:', error);
      return 'America/New_York';
    }
  };

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDigestPreferences();
      setPreferences(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
      console.error('Error loading digest preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (enabled: boolean) => {
    setPreferences(prev => ({ ...prev, digest_enabled: enabled }));
  };

  const handleTimezoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPreferences(prev => ({ ...prev, digest_timezone: e.target.value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await updateDigestPreferences(preferences);

      setSuccess(response.message || 'Preferences saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
      console.error('Error saving digest preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    try {
      setSendingTest(true);
      setError(null);
      setSuccess(null);
      
      const result = await sendTestEmail();
      
      setSuccess('Test email sent! Check your inbox in a few moments.');

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test email');
      console.error('Error sending test email:', err);
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center py-8">
          <div className="cgm-text-secondary">Loading preferences...</div>
        </div>
      </div>
    );
  }

  // Common US timezones
  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST - no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
  ];

  return (
    <div className={className}>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 rounded-md cgm-alert-error">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-4 p-3 rounded-md cgm-alert-success">
          <p className="text-sm">{success}</p>
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="mb-6">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => handleToggle(!preferences.digest_enabled)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
              preferences.digest_enabled ? 'bg-green-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
            }`}
            aria-label="Toggle daily digest emails"
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                preferences.digest_enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <div className="ml-3">
            <span className="text-sm font-medium cgm-text-primary">
              Enable Daily Digest Emails
            </span>
            <p className="text-xs cgm-text-secondary">
              {preferences.digest_enabled ? 'You will receive daily emails at 10:00 AM' : 'Email digests are disabled'}
            </p>
          </div>
        </div>
      </div>

      {/* Timezone Display/Selector */}
      {preferences.digest_enabled && (
        <div className="mb-6">
          {!showTimezoneSelector ? (
            // Auto-detected timezone display (default)
            <div className="flex items-center justify-between py-2 px-3 rounded-md cgm-list-item">
              <div>
                <p className="text-sm cgm-text-primary">
                  You'll receive a daily email at 10:00 AM {timezones.find(tz => tz.value === preferences.digest_timezone)?.label}
                </p>
                <p className="text-xs cgm-text-muted mt-1">
                  Timezone auto-detected from your browser
                </p>
              </div>
              <button
                onClick={() => setShowTimezoneSelector(true)}
                className="text-sm font-medium cgm-text-secondary hover:cgm-text-primary transition-colors"
              >
                Change
              </button>
            </div>
          ) : (
            // Manual timezone selector (shown when user clicks "Change")
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium cgm-text-secondary">
                  Timezone
                </label>
                <button
                  onClick={() => setShowTimezoneSelector(false)}
                  className="text-xs cgm-text-secondary hover:cgm-text-primary transition-colors"
                >
                  Use auto-detected
                </button>
              </div>
              <select
                value={preferences.digest_timezone}
                onChange={handleTimezoneChange}
                className="cgm-input w-full px-3 py-2"
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-sm cgm-text-muted">
                You'll receive a daily email at 10:00 AM {timezones.find(tz => tz.value === preferences.digest_timezone)?.label}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Test Email Button */}
        <button
          onClick={handleSendTest}
          disabled={sendingTest || !preferences.digest_enabled}
          title={!preferences.digest_enabled ? 'Enable email digests first to test' : 'Send a test email to yourself now'}
          className={`cgm-button flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            sendingTest || !preferences.digest_enabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {sendingTest ? (
            <>
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send Test Email Now
            </>
          )}
        </button>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`cgm-button flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
            saving ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
      
      {!preferences.digest_enabled && (
        <p className="text-xs text-amber-600 text-center mt-2">
          Enable digests above to send a test email
        </p>
      )}
    </div>
  );
};

export default DigestPreferences;
